import React, { useState, useEffect } from 'react';
import { generationsAPI } from '../services/api';
import Lightbox from '../components/Lightbox';
import './Gallery.css';

// Clean up error messages for display
const getCleanErrorMessage = (errorMessage) => {
  if (!errorMessage) return 'Generation failed. Please try again.';

  // Check for common error patterns and return user-friendly messages
  if (errorMessage.includes('504') || errorMessage.includes('Gateway time-out')) {
    return 'The service was temporarily unavailable. Please try again.';
  }
  if (errorMessage.includes('502') || errorMessage.includes('503')) {
    return 'The service is experiencing high demand. Please try again later.';
  }
  if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
    return 'Request timed out. Please try again.';
  }
  if (errorMessage.includes('429')) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (errorMessage.includes('Insufficient credits')) {
    return 'Insufficient credits. Please purchase more credits.';
  }
  if (errorMessage.includes('Failed to upload')) {
    return 'Failed to upload image. Please try again.';
  }
  if (errorMessage.includes('<!DOCTYPE') || errorMessage.includes('<html')) {
    return 'Service temporarily unavailable. Please try again.';
  }

  // If the message is too long (likely contains HTML or technical details), truncate it
  if (errorMessage.length > 100) {
    return 'Generation failed. Please try again.';
  }

  return errorMessage;
};

function Gallery() {
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchGenerations();
  }, [page]);

  const fetchGenerations = async () => {
    try {
      const response = await generationsAPI.list(page, 12);
      setGenerations(response.data.generations);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch generations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'badge-success',
      processing: 'badge-warning',
      failed: 'badge-danger',
      pending: 'badge-primary',
    };
    return badges[status] || 'badge-primary';
  };

  const handleDownload = async (generationId) => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/generations/${generationId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `redoit-design-${generationId}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page gallery-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Your Gallery</h1>
          <p className="page-description">
            View all your generated designs
          </p>
        </div>

        {generations.length > 0 ? (
          <>
            <div className="gallery-grid">
              {generations.map((gen) => (
                <div
                  key={gen.id}
                  className="gallery-card card"
                  onClick={() => setSelectedGeneration(gen)}
                >
                  <div className="gallery-image">
                    {gen.output_image_url ? (
                      <img src={gen.output_image_url} alt="Generated design" />
                    ) : (
                      <div className="gallery-placeholder">
                        {gen.status === 'processing' ? (
                          <div className="spinner" />
                        ) : (
                          <span className="placeholder-text">
                            {gen.status === 'failed' ? 'Failed' : 'Pending'}
                          </span>
                        )}
                      </div>
                    )}
                    <span className={`badge ${getStatusBadge(gen.status)}`}>
                      {gen.status}
                    </span>
                  </div>
                  <div className="gallery-info">
                    <span className="gallery-type">
                      {gen.generation_type.replace('_', ' ')}
                    </span>
                    {gen.material_type && (
                      <span className="gallery-material">
                        {gen.material_type.replace('_', ' ')}
                      </span>
                    )}
                    <span className="gallery-date">
                      {new Date(gen.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <span className="empty-state-icon">🖼️</span>
            <h3 className="empty-state-title">No designs yet</h3>
            <p className="empty-state-text">
              Start creating AI-powered room designs
            </p>
            <a href="/generate" className="btn btn-primary">
              Create Your First Design
            </a>
          </div>
        )}

        {/* Detail Modal */}
        {selectedGeneration && (
          <div
            className="modal-overlay"
            onClick={() => setSelectedGeneration(null)}
          >
            <div className="modal gallery-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">
                  {selectedGeneration.generation_type.replace('_', ' ')}
                </h2>
                <button
                  className="modal-close"
                  onClick={() => setSelectedGeneration(null)}
                >
                  ×
                </button>
              </div>

              <div className="modal-comparison">
                <div className="comparison-image">
                  <span className="comparison-label">Before</span>
                  <div
                    className="clickable-image"
                    onClick={() => {
                      setLightboxIndex(0);
                      setLightboxOpen(true);
                    }}
                  >
                    <img
                      src={selectedGeneration.input_image_url}
                      alt="Before"
                    />
                    <div className="image-overlay">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                        <path d="M11 8v6M8 11h6" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="comparison-image">
                  <span className="comparison-label">After</span>
                  {selectedGeneration.output_image_url ? (
                    <div
                      className="clickable-image"
                      onClick={() => {
                        setLightboxIndex(1);
                        setLightboxOpen(true);
                      }}
                    >
                      <img
                        src={selectedGeneration.output_image_url}
                        alt="After"
                      />
                      <div className="image-overlay">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" />
                          <path d="M21 21l-4.35-4.35" />
                          <path d="M11 8v6M8 11h6" />
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <div className="comparison-placeholder">
                      {selectedGeneration.status === 'failed'
                        ? getCleanErrorMessage(selectedGeneration.error_message)
                        : 'Processing...'}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-details">
                {selectedGeneration.material_type && (
                  <div className="detail-item">
                    <span className="detail-label">Material:</span>
                    <span className="detail-value">
                      {selectedGeneration.material_type.replace('_', ' ')}
                    </span>
                  </div>
                )}
                {selectedGeneration.color && (
                  <div className="detail-item">
                    <span className="detail-label">Color:</span>
                    <span className="detail-value">{selectedGeneration.color}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">
                    {new Date(selectedGeneration.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Credits Used:</span>
                  <span className="detail-value">
                    {selectedGeneration.credits_used}
                  </span>
                </div>
              </div>

              {selectedGeneration.output_image_url && (
                <div className="modal-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => handleDownload(selectedGeneration.id)}
                  >
                    Download Image
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Lightbox for full-size image viewing */}
        {lightboxOpen && selectedGeneration && (
          <Lightbox
            images={[
              {
                url: selectedGeneration.input_image_url,
                label: 'Before',
                alt: 'Original room'
              },
              ...(selectedGeneration.output_image_url ? [{
                url: selectedGeneration.output_image_url,
                label: 'After',
                alt: 'AI transformed room'
              }] : [])
            ]}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

export default Gallery;
